import * as React from 'react';
import {
    FormGroup,
    FormControl,
    ControlLabel,
    HelpBlock,
    Button,
    Row,
    Col,
    Image
} from 'react-bootstrap';
import { useFeatureFlag } from '../../shared/featureFlags/useFeatureFlag';
import { FeatureFlagKeys } from '../../shared/featureFlags/featureFlagKeys';
import { Translate } from '../../shared/LanguageService';
import LanguageSelect from '../../components/common/LanguageSelect';
import FormSelect from '../../components/common/FormSelect';
import FieldInfoIcon from '../../components/common/FieldInfoIcon';
import FormValidationService from '../../shared/form/FormValidationService';
import CountryCodePhoneNumberInput from '../../components/common/CountryCodePhoneNumberInput';
import IProfileType from './IProfileType';

interface IProfileFormProps {
    user: any;
    profile: IProfileType;
    timeZones: { id: string; name: string }[];
    errors: any;
    saving: boolean;
    onChange: (e: any) => void;
    save: () => void;
    imagePreview: any;
    onImageChange: (e: any) => void;
    availableLanguages: any[];
    allowedToChangeUserName: boolean;
}

const ProfileForm = (props: IProfileFormProps) => {
    let isProfileModalEnabled = useFeatureFlag(FeatureFlagKeys.ShowProfileModal, false);

    return (
        <div>
            {props.imagePreview && (
                <Row>
                    <Col className="profile-image-preview" md={3} xs={6}>
                        {props.imagePreview}
                    </Col>
                </Row>
            )}
            <Row>
                <Col md={6} xs={6}>
                    <FormGroup>
                        <ControlLabel>{Translate('profile/page/label/profile-image')}</ControlLabel>
                        <FormControl type="file" onChange={props.onImageChange} />
                    </FormGroup>
                </Col>
            </Row>
            {!isProfileModalEnabled ? (
                <Row>
                    <Col md={6}>
                        <FormGroup
                            validationState={FormValidationService.getValidationState(
                                props.errors.name
                            )}
                        >
                            <ControlLabel>{Translate('profile/form/name-label')}</ControlLabel>
                            <FormControl
                                type="text"
                                name="name"
                                value={props.profile.name ? props.profile.name : ''}
                                onChange={props.onChange}
                            />
                            <HelpBlock>{props.errors.name}</HelpBlock>
                        </FormGroup>
                    </Col>
                </Row>
            ) : null}
            {!isProfileModalEnabled ? (
                <Row>
                    <Col md={6}>
                        <FormGroup
                            validationState={FormValidationService.getValidationState(
                                props.errors.username
                            )}
                        >
                            <ControlLabel>{Translate('profile/form/username-label')}</ControlLabel>
                            <FormControl
                                type="text"
                                name="username"
                                value={props.profile.username ? props.profile.username : ''}
                                onChange={props.onChange}
                                disabled={!props.allowedToChangeUserName}
                            />
                            <HelpBlock>{props.errors.username}</HelpBlock>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup
                            validationState={FormValidationService.getValidationState(
                                props.errors.email
                            )}
                        >
                            <ControlLabel>{Translate('profile/form/email-label')}</ControlLabel>
                            <FormControl
                                type="email"
                                name="email"
                                value={props.profile.email ? props.profile.email : ''}
                                onChange={props.onChange}
                            />
                            <HelpBlock>{props.errors.email}</HelpBlock>
                        </FormGroup>
                        <FormGroup
                            validationState={FormValidationService.getValidationState(
                                props.errors.phoneNumber
                            )}
                        >
                            <ControlLabel>
                                {Translate('profile/form/phoneNumber-label')}
                            </ControlLabel>
                            <FieldInfoIcon
                                title={Translate('popover/info/title/phonenumber')}
                                content={Translate('popover/info/content/phonenumber')}
                            />
                            <CountryCodePhoneNumberInput
                                value={props.profile.phoneNumber ? props.profile.phoneNumber : ''}
                                onChange={(val) =>
                                    props.onChange({
                                        target: { name: 'phoneNumber', value: val }
                                    })
                                }
                            />
                            <HelpBlock>{props.errors.phoneNumber}</HelpBlock>
                        </FormGroup>
                    </Col>
                </Row>
            ) : null}
            {!isProfileModalEnabled ? (
                <Row>
                    <Col md={6}>
                        <LanguageSelect
                            onChange={props.onChange}
                            languageKey={props.profile.languageKey}
                            errors={props.errors}
                            languages={props.availableLanguages}
                        />
                    </Col>
                    <Col md={6}>
                        <FormGroup
                            validationState={FormValidationService.getValidationState(
                                props.errors.timeZoneId
                            )}
                        >
                            <ControlLabel>{Translate('profile/form/timeZone-label')}</ControlLabel>
                            <FormSelect
                                name="timeZoneId"
                                onChange={props.onChange}
                                value={props.profile.timeZoneId ? props.profile.timeZoneId : '0'}
                                defaultTranslateKey="profile/form/timezone/select-timezone"
                            >
                                {props.timeZones.map((t: { id: string; name: string }) => {
                                    return (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    );
                                })}
                            </FormSelect>
                            <HelpBlock>{props.errors.timeZoneId}</HelpBlock>
                        </FormGroup>
                    </Col>
                </Row>
            ) : null}
            <div>
                <Button
                    className="pull-right"
                    disabled={props.saving}
                    bsSize="small"
                    bsStyle="success"
                    onClick={props.save}
                >
                    {Translate('profile/form/save-button/label')}
                </Button>
            </div>
        </div>
    );
};

export default ProfileForm;
